/**
 * MinistrareIA - File Upload Service
 * Service for uploading files to Azure Blob Storage
 */

import api from '../global/api';

const FILES_BASE = '/api/files';

// Allowed file types
const ALLOWED_TYPES = {
  // Images
  'image/jpeg': { type: 'image', maxSize: 25 },
  'image/png': { type: 'image', maxSize: 25 },
  'image/gif': { type: 'image', maxSize: 25 },
  'image/webp': { type: 'image', maxSize: 25 },
  
  // Videos
  'video/mp4': { type: 'video', maxSize: 25 },
  'video/webm': { type: 'video', maxSize: 25 },
  'video/quicktime': { type: 'video', maxSize: 25 },
  
  // Audio
  'audio/mpeg': { type: 'audio', maxSize: 25 },
  'audio/mp3': { type: 'audio', maxSize: 25 },
  'audio/wav': { type: 'audio', maxSize: 25 },
  'audio/ogg': { type: 'audio', maxSize: 25 },
  'audio/mp4': { type: 'audio', maxSize: 25 },
  
  // Documents
  'application/pdf': { type: 'pdf', maxSize: 25 },
  'application/msword': { type: 'document', maxSize: 25 },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { type: 'document', maxSize: 25 },
  'application/vnd.ms-excel': { type: 'document', maxSize: 25 },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { type: 'document', maxSize: 25 },
  'application/vnd.ms-powerpoint': { type: 'document', maxSize: 25 },
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': { type: 'document', maxSize: 25 },
  'text/plain': { type: 'document', maxSize: 25 },
  'text/csv': { type: 'document', maxSize: 25 },
  'application/zip': { type: 'document', maxSize: 25 },
};

// Max file size in MB
const MAX_FILE_SIZE_MB = 25;

/**
 * File Upload Service
 */
class FileUploadService {
  /**
   * Validate a file before upload
   * @param {File} file - File object from input
   * @returns {Object} { valid: boolean, error?: string, type?: string }
   */
  validateFile(file) {
    if (!file) {
      return { valid: false, error: 'Nenhum arquivo selecionado' };
    }

    // Check file size
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > MAX_FILE_SIZE_MB) {
      return { 
        valid: false, 
        error: `Arquivo muito grande. Máximo: ${MAX_FILE_SIZE_MB}MB` 
      };
    }

    if (file.size === 0) {
      return { valid: false, error: 'Arquivo vazio' };
    }

    // Check file type
    const typeInfo = ALLOWED_TYPES[file.type];
    if (!typeInfo) {
      // Try to detect by extension
      const ext = file.name.split('.').pop()?.toLowerCase();
      const extTypeMap = {
        jpg: 'image', jpeg: 'image', png: 'image', gif: 'image', webp: 'image',
        mp4: 'video', webm: 'video', mov: 'video', avi: 'video',
        mp3: 'audio', wav: 'audio', ogg: 'audio', m4a: 'audio',
        pdf: 'pdf', doc: 'document', docx: 'document', 
        xls: 'document', xlsx: 'document', ppt: 'document', pptx: 'document',
        txt: 'document', csv: 'document', zip: 'document',
      };

      if (!extTypeMap[ext]) {
        return { 
          valid: false, 
          error: 'Tipo de arquivo não suportado. Use: imagens, vídeos, áudios ou documentos.' 
        };
      }

      return { valid: true, type: extTypeMap[ext] };
    }

    return { valid: true, type: typeInfo.type };
  }

  /**
   * Upload a single file
   * @param {File} file - File object from input
   * @param {Function} onProgress - Progress callback (0-100)
   * @returns {Promise<Object>} Uploaded file info
   */
  async uploadFile(file, onProgress = null) {
    // Validate first
    const validation = this.validateFile(file);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    // Create FormData
    const formData = new FormData();
    formData.append('file', file);

    console.log('[FileUpload] Uploading:', {
      name: file.name,
      type: file.type,
      size: `${(file.size / 1024).toFixed(1)}KB`,
    });

    try {
      const response = await api.post(`${FILES_BASE}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(percent);
          }
        },
      });

      if (!response.data?.success) {
        throw new Error(response.data?.detail || 'Upload failed');
      }

      const data = response.data.data;
      
      console.log('[FileUpload] Success:', data.url);

      return {
        url: data.url,
        filename: data.filename,
        type: data.type,
        mimeType: data.mimeType,
        size: data.size,
      };
    } catch (error) {
      console.error('[FileUpload] Error:', error);
      
      // Extract error message
      const message = error.response?.data?.detail || 
                      error.message || 
                      'Erro ao fazer upload do arquivo';
      
      throw new Error(message);
    }
  }

  /**
   * Upload multiple files
   * @param {FileList|File[]} files - Files to upload
   * @param {Function} onProgress - Progress callback ({ uploaded, total, currentFile })
   * @returns {Promise<Object>} { uploaded: [], errors: [] }
   */
  async uploadMultiple(files, onProgress = null) {
    // Handle both FileList and Array
    const fileArray = files instanceof FileList ? Array.from(files) : files;
    
    if (!fileArray || fileArray.length === 0) {
      throw new Error('Nenhum arquivo selecionado');
    }

    if (fileArray.length > 10) {
      throw new Error('Máximo de 10 arquivos por vez');
    }

    console.log('[FileUpload] uploadMultiple called with:', fileArray.length, 'files');

    const results = {
      uploaded: [],
      errors: [],
    };

    for (let i = 0; i < fileArray.length; i++) {
      const file = fileArray[i];
      
      // Report progress
      if (onProgress) {
        onProgress({
          uploaded: i,
          total: fileArray.length,
          currentFile: file.name,
        });
      }

      try {
        const result = await this.uploadFile(file);
        results.uploaded.push(result);
      } catch (error) {
        results.errors.push({
          filename: file.name,
          error: error.message,
        });
      }
    }

    // Final progress
    if (onProgress) {
      onProgress({
        uploaded: fileArray.length,
        total: fileArray.length,
        currentFile: null,
      });
    }

    return results;
  }

  /**
   * Get accept string for file input
   * @returns {string} Accept attribute value
   */
  getAcceptString() {
    return [
      // Images
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      // Videos
      'video/mp4',
      'video/webm',
      'video/quicktime',
      // Audio
      'audio/mpeg',
      'audio/mp3',
      'audio/wav',
      'audio/ogg',
      'audio/mp4',
      // Documents
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain',
      'text/csv',
      '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip',
    ].join(',');
  }

  /**
   * Format file size for display
   * @param {number} bytes - Size in bytes
   * @returns {string} Formatted size
   */
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }
}

// Export singleton
const fileUploadService = new FileUploadService();
export default fileUploadService;